import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import SealPassport from '../components/SealPassport';
import Certificate from '../components/Certificate';
import Booklet from '../components/Booklet';
import AILiteracyBooklet from '../components/AILiteracyBooklet';

const TABS = [
  { key: 'stamps', icon: '🌟', labelEn: 'My Seals', labelJa: '私のシール' },
  { key: 'booklet', icon: '📖', labelEn: 'My Booklet', labelJa: 'マイブックレット' },
  { key: 'certificate', icon: '🏆', labelEn: 'Certificate', labelJa: '修了証' },
];

export default function StudentProfile() {
  const { slug } = useParams();
  const { t, lang, user, api } = useApp();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [activeTab, setActiveTab] = useState('stamps');
  const [activeProgram, setActiveProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [threshold, setThreshold] = useState(100);
  // Session history — teacher/admin only. `null` = the student's live/current session.
  const [terms, setTerms] = useState([]);
  const [selectedTerm, setSelectedTerm] = useState(null);

  const isStaffViewer = user?.role === 'teacher' || user?.role === 'admin';
  const [forcingCert, setForcingCert] = useState(false);

  const forceCertificate = async () => {
    if (!profile) return;
    const next = !profile.certificate_unlocked;
    setForcingCert(true);
    try {
      await api('patch', `/admin/students/${profile.id}/force-certificate`, { unlocked: next });
      setProfile(p => ({ ...p, certificate_unlocked: next ? 1 : 0 }));
    } catch {}
    setForcingCert(false);
  };

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    loadProfile();
  }, [user, slug, selectedTerm]);

  useEffect(() => {
    if (!user || !slug || !isStaffViewer) return;
    api('get', `/student/profile/${slug}/terms`).then(res => setTerms(res.data.terms || [])).catch(() => {});
  }, [user, slug]);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const base = slug ? `/student/profile/${slug}` : '/student/profile';
      const url = selectedTerm ? `${base}?term=${selectedTerm}` : base;
      const [profRes, settRes] = await Promise.all([
        api('get', url),
        api('get', '/admin/settings').catch(() => ({ data: {} })),
      ]);
      setProfile(profRes.data);
      if (profRes.data.programs?.length) setActiveProgram(profRes.data.programs[0]);
      if (settRes.data?.certificate_threshold) setThreshold(parseInt(settRes.data.certificate_threshold));
    } catch (err) {
      if (err.response?.status === 403) setError('Access denied.');
      else setError(t.error);
    } finally { setLoading(false); }
  };

  if (loading) return (
    <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '60vh' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '3rem', animation: 'spin 1s linear infinite' }}>⭐</div>
        <div style={{ fontWeight: 700, marginTop: '1rem', color: 'var(--muted)' }}>{t.loading}</div>
      </div>
    </div>
  );

  if (error) return (
    <div className="container py-5 text-center">
      <div style={{ fontSize: '3rem' }}>😢</div>
      <h3 style={{ fontWeight: 800 }}>{error}</h3>
      <button onClick={() => navigate(-1)} className="btn-summer mt-3">{t.backHome}</button>
    </div>
  );

  if (!profile) return null;

  const displayName = lang === 'ja' ? (profile.name_ja || profile.name) : profile.name;
  const ROUTINE_SESSIONS = ['cleaning', 'warmup', 'brush', 'boardgames'];
  const allStamps = profile.stamps || [];
  const stamps = allStamps.filter(s => !ROUTINE_SESSIONS.includes(s.session));
  const routineStamps = allStamps.filter(s => ROUTINE_SESSIONS.includes(s.session));
  const awards = profile.awards || [];
  const activities = profile.activities || [];

  const programAwards = activeProgram ? awards.filter(a => a.program_id === activeProgram.id) : [];
  // Grand percentage: core program stamps + daily routine stamps.
  // Only stamps from CURRENTLY enrolled core programs count — a student who
  // was later removed from a program keeps that program's old stamp rows,
  // which must not keep inflating the score against a shrunk denominator.
  const corePrograms = (profile.programs || []).filter(p => p.slug !== 'daily-routine');
  const coreProgramIds = new Set(corePrograms.map(p => p.id));
  const coreStamps = stamps.filter(s => coreProgramIds.has(s.program_id));
  const ROUTINE_MAX = 5 * 4 * 100; // 5 days × 4 activities × 100
  const PROGRAM_MAX = 10 * 100; // 10 slots (5 days × morning/afternoon) × 100
  // Each program's — and the routine's — contribution is capped at its own
  // max before summing. Without this, stray/duplicate stamp rows in one
  // program (e.g. leftover from a data issue) could push that program's raw
  // score past its own 100%, which would drag the combined total over 100%
  // too and falsely unlock the certificate for a student who isn't done.
  const totalScore = corePrograms.reduce((sum, p) => {
    const programScore = coreStamps.filter(s => s.program_id === p.id).reduce((s, st) => s + (st.score || 0), 0);
    return sum + Math.min(programScore, PROGRAM_MAX);
  }, 0) + Math.min(routineStamps.reduce((sum, s) => sum + (s.score || 100), 0), ROUTINE_MAX);
  const maxPossible = corePrograms.length * PROGRAM_MAX + ROUTINE_MAX;
  const grandPct = maxPossible > 0 ? Math.min(100, Math.round((totalScore / maxPossible) * 100)) : 0;
  const isComplete = grandPct >= threshold || !!profile.certificate_unlocked;
  // Teachers/admins always see the certificate preview (alongside the progress
  // bar), regardless of completion. Students only see the actual certificate
  // once they hit the completion threshold — below that, only the progress bar.
  const viewingPastSession = isStaffViewer && profile.viewing_term && profile.current_term && profile.viewing_term !== profile.current_term;

  return (
    <div className="summer-bg pb-5">
      {/* Teacher/Admin viewing banner */}
      {isStaffViewer && (
        <div style={{ background: '#073B4C', color: 'white', padding: '0.6rem 0' }}>
          <div className="container d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div style={{ fontWeight: 700, fontSize: '0.82rem' }}>
              {user.role === 'admin' ? '⚙️ Admin' : '👩‍🏫 Teacher'} {lang === 'ja' ? '閲覧モード' : 'View'}
              <span style={{ opacity: 0.6, marginLeft: 8 }}>— {lang === 'ja' ? '生徒のプロフィールを見ています' : "Viewing student's profile"}</span>
            </div>
            <button onClick={forceCertificate} disabled={forcingCert}
              style={{ background: profile.certificate_unlocked ? '#06D6A0' : '#FFD166', color: '#073B4C', border: 'none', borderRadius: 999, padding: '0.3rem 1rem', fontWeight: 900, fontSize: '0.78rem', cursor: 'pointer' }}>
              {forcingCert ? '...' : profile.certificate_unlocked ? (lang === 'ja' ? '🏆 修了証を解除' : '🏆 Revoke Certificate') : (lang === 'ja' ? '🔓 修了証を強制解放' : '🔓 Force Unlock Certificate')}
            </button>
          </div>
        </div>
      )}
      {/* Profile header */}
      <div style={{ background: 'linear-gradient(135deg, #FF6B35 0%, #FFD166 50%, #06D6A0 100%)', padding: '3rem 0', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'url("data:image/svg+xml,%3Csvg width=\'40\' height=\'40\' viewBox=\'0 0 40 40\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.08\'%3E%3Ccircle cx=\'20\' cy=\'20\' r=\'3\'/%3E%3C/g%3E%3C/svg%3E")' }} />
        <div className="container position-relative">
          <div className="d-flex align-items-center gap-4 flex-wrap">
            {/* Avatar */}
            <div style={{
              width: 88, height: 88, borderRadius: '50%', flexShrink: 0,
              background: 'rgba(255,255,255,0.25)',
              border: '4px solid white',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '2.5rem', backdropFilter: 'blur(10px)',
            }}>
              {profile.avatar_url ? <img src={profile.avatar_url} alt="" style={{ width: '100%', borderRadius: '50%' }} /> : '👤'}
            </div>
            <div style={{ color: 'white' }}>
              <h2 style={{ fontWeight: 900, fontSize: '2rem', margin: 0 }}>{displayName}</h2>
              <div style={{ opacity: 0.85, fontWeight: 600, marginTop: '0.3rem' }}>
                {t.studentProfile} · {t.enrolledIn} {profile.programs?.length || 0} {t.programs}
              </div>
              {isComplete && (
                <div style={{ marginTop: '0.5rem', display: 'inline-flex', alignItems: 'center', gap: '0.4rem', background: 'rgba(255,255,255,0.2)', borderRadius: 50, padding: '0.3rem 1rem', fontSize: '0.85rem', fontWeight: 700 }}>
                  🏆 {lang === 'ja' ? 'プログラム修了！' : 'Program Completed!'}
                </div>
              )}
            </div>
            {/* Grand % badge */}
            <div className="ms-auto text-center" style={{ color: 'white' }}>
              <div style={{ fontSize: '2.5rem', fontWeight: 900, lineHeight: 1 }}>{grandPct}%</div>
              <div style={{ fontSize: '0.7rem', opacity: 0.85, fontWeight: 700, marginTop: 2 }}>
                {lang === 'ja' ? '総合スコア' : 'Overall Score'}
              </div>
              {isComplete && <div style={{ fontSize: '0.72rem', marginTop: 3 }}>🏆 {lang === 'ja' ? '修了！' : 'Certified!'}</div>}
            </div>
          </div>
        </div>
      </div>

      <div className="container mt-4">
        {/* Session history — teacher/admin only */}
        {isStaffViewer && terms.length > 0 && (
          <div className="d-flex align-items-center gap-2 flex-wrap mb-3">
            <span style={{ fontWeight: 800, fontSize: '0.82rem', color: 'var(--muted)' }}>
              📅 {lang === 'ja' ? 'セッション：' : 'Session:'}
            </span>
            <select
              className="input-summer"
              style={{ width: 'auto', padding: '0.4rem 0.8rem', fontSize: '0.85rem', fontWeight: 700 }}
              value={selectedTerm || ''}
              onChange={e => setSelectedTerm(e.target.value ? parseInt(e.target.value) : null)}
            >
              {terms.map(tm => (
                <option key={tm.term} value={tm.term === profile.current_term ? '' : tm.term}>
                  {tm.term === profile.current_term
                    ? (lang === 'ja' ? `現在のセッション（${tm.stamp_count}スタンプ）` : `Current session (${tm.stamp_count} stamps)`)
                    : (lang === 'ja'
                        ? `過去のセッション #${tm.term}（${tm.stamp_count}スタンプ）`
                        : `Past session #${tm.term} (${tm.stamp_count} stamps)`)}
                </option>
              ))}
            </select>
            {viewingPastSession && (
              <span style={{ background: '#FEF3C7', color: '#92400E', fontWeight: 800, fontSize: '0.78rem', padding: '0.3rem 0.8rem', borderRadius: 999 }}>
                {lang === 'ja' ? '⚠ 過去の記録を表示中（読み取り専用）' : '⚠ Viewing past session (read-only)'}
              </span>
            )}
          </div>
        )}

        {/* Main content tabs */}
        <div className="mb-3">
          <div className="tab-nav">
            {TABS.map(tab => (
              <button key={tab.key} className={`tab-btn ${activeTab === tab.key ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.key)}>
                {tab.icon} {lang === 'ja' ? tab.labelJa : tab.labelEn}
              </button>
            ))}
          </div>
        </div>

        {/* Program tabs — shown in Seals tab */}
        {activeTab === 'stamps' && profile.programs?.length > 0 && (
          <div className="mb-4">
            <div className="tab-nav" style={{ flexWrap: 'wrap', gap: '0.4rem' }}>
              {profile.programs.map(p => (
                <button key={p.id}
                  className={`tab-btn ${activeProgram?.id === p.id ? 'active' : ''}`}
                  onClick={() => setActiveProgram(p)}
                  style={{ display: 'flex', alignItems: 'center', gap: 5 }}
                >
                  {p.icon && <span>{p.icon}</span>}
                  {lang === 'ja' ? (p.name_ja || p.name) : p.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* SEALS TAB */}
        {activeTab === 'stamps' && (
          <div>
            <SealPassport
              stamps={stamps}
              routineStamps={routineStamps}
              program={activeProgram}
              allPrograms={profile.programs || []}
              studentName={displayName}
              lang={lang}
              threshold={threshold}
              grandPct={grandPct}
            />

            {/* Awards */}
            {programAwards.length > 0 && (
              <div className="card-summer p-4 mt-4">
                <h6 style={{ fontWeight: 800, color: 'var(--orange)', marginBottom: '1rem' }}>🏆 {t.awards}</h6>
                <div className="d-flex flex-wrap gap-2">
                  {programAwards.map((a, i) => (
                    <div key={i} className="award-badge">
                      🏆 {lang === 'ja' ? (a.award_label_ja || a.award_label) : a.award_label}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* BOOKLET TAB — only render booklets for programs that have one */}
        {activeTab === 'booklet' && profile.programs?.length > 0 && (
          <div>
            {profile.programs
              .filter(prog => prog.slug !== 'daily-routine')
              .map(prog => {
                if (prog.slug === 'ai-literacy') {
                  return (
                    <AILiteracyBooklet
                      key={prog.id}
                      student={profile}
                      stamps={stamps}
                      activities={activities}
                      programId={prog.id}
                    />
                  );
                }
                if (prog.slug === 'kids-entrepreneurship') {
                  return (
                    <Booklet
                      key={prog.id}
                      student={profile}
                      stamps={stamps}
                      activities={activities}
                      programId={prog.id}
                    />
                  );
                }
                // Other programs don't have a booklet yet
                return (
                  <div key={prog.id} className="card-summer p-5 text-center mb-4">
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>{prog.icon || '📖'}</div>
                    <h5 style={{ fontWeight: 800, color: 'var(--muted)' }}>
                      {lang === 'ja' ? (prog.name_ja || prog.name) : prog.name}
                    </h5>
                    <p style={{ color: 'var(--muted)', fontWeight: 600, marginTop: '0.5rem' }}>
                      {lang === 'ja' ? 'このプログラムのブックレットは近日公開予定です！' : 'Booklet for this program coming soon!'}
                    </p>
                  </div>
                );
              })
            }
          </div>
        )}

        {/* CERTIFICATE TAB */}
        {activeTab === 'certificate' && (
          <div>
            {isStaffViewer ? (
              <>
                {/* Teachers/admins always see the certificate preview, complete or not */}
                <Certificate
                  student={profile}
                  programs={corePrograms}
                  stamps={stamps}
                  awards={awards}
                />
                <div className="card-summer p-4 mt-4 text-center">
                  <div style={{ fontWeight: 800, color: 'var(--muted)', marginBottom: '0.5rem' }}>
                    {lang === 'ja' ? '総合進捗' : 'Overall Progress'}
                  </div>
                  <div className="progress-summer" style={{ maxWidth: 300, margin: '0 auto' }}>
                    <div className="progress-summer-bar" style={{ width: `${grandPct}%` }} />
                  </div>
                  <div style={{ fontWeight: 700, color: 'var(--orange)', marginTop: '0.5rem' }}>
                    {grandPct}% / {threshold}%
                  </div>
                </div>
              </>
            ) : isComplete ? (
              <>
                {/* Student has hit the completion threshold — show the certificate plus their progress */}
                <Certificate
                  student={profile}
                  programs={corePrograms}
                  stamps={stamps}
                  awards={awards}
                />
                <div className="card-summer p-4 mt-4 text-center">
                  <div style={{ fontWeight: 800, color: 'var(--muted)', marginBottom: '0.5rem' }}>
                    {lang === 'ja' ? '総合進捗' : 'Overall Progress'}
                  </div>
                  <div className="progress-summer" style={{ maxWidth: 300, margin: '0 auto' }}>
                    <div className="progress-summer-bar" style={{ width: `${grandPct}%` }} />
                  </div>
                  <div style={{ fontWeight: 700, color: 'var(--orange)', marginTop: '0.5rem' }}>
                    {grandPct}% / {threshold}%
                  </div>
                </div>
              </>
            ) : (
              // Not complete yet — only the progress bar, no certificate or preview.
              <div className="card-summer p-5 text-center">
                <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🔒</div>
                <h4 style={{ fontWeight: 800, color: 'var(--muted)' }}>
                  {lang === 'ja' ? 'まだ修了証は発行されていません' : 'Certificate not yet earned'}
                </h4>
                <p style={{ color: 'var(--muted)', fontWeight: 600 }}>
                  {lang === 'ja'
                    ? `あと ${threshold - grandPct}% 達成すると修了証が発行されます！`
                    : `Reach ${threshold - grandPct}% more to unlock your certificate!`}
                </p>
                <div className="progress-summer mt-3" style={{ maxWidth: 300, margin: '0 auto' }}>
                  <div className="progress-summer-bar" style={{ width: `${grandPct}%` }} />
                </div>
                <div style={{ fontWeight: 700, color: 'var(--orange)', marginTop: '0.5rem' }}>
                  {grandPct}% / {threshold}%
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
